import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import AdminLayout from '../../components/AdminLayout';

function AdminUserManagement() {
  const [subscribedUsers, setSubscribedUsers] = useState([]);
  const [unsubscribedUsers, setUnsubscribedUsers] = useState([]);
  const [loadingSubscribed, setLoadingSubscribed] = useState(true);
  const [loadingUnsubscribed, setLoadingUnsubscribed] = useState(true);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState('');

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSubscribedUsers = useCallback(async () => {
    setLoadingSubscribed(true);
    try {
      const response = await axios.get(`${API_URL}/subscriptions/active`, { headers });
      setSubscribedUsers(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching subscribed users:', err);
      setError(err.response?.data?.error || 'Failed to load subscribed users.');
    }
    setLoadingSubscribed(false);
  }, [token]); // headers is not stable, token is

  const fetchUnsubscribedUsers = useCallback(async () => {
    setLoadingUnsubscribed(true);
    try {
      const response = await axios.get(`${API_URL}/subscriptions/inactive`, { headers });
      setUnsubscribedUsers(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching unsubscribed users:', err);
      setError(err.response?.data?.error || 'Failed to load unsubscribed users.');
    }
    setLoadingUnsubscribed(false);
  }, [token]);

  useEffect(() => {
    fetchSubscribedUsers();
    fetchUnsubscribedUsers();
  }, [fetchSubscribedUsers, fetchUnsubscribedUsers]);

  const handleUnsubscribeUser = async (email) => {
    if (!window.confirm(`Are you sure you want to unsubscribe ${email}?`)) {
      return;
    }
    try {
      setActionMessage('');
      await axios.delete(`${API_URL}/subscriptions/admin/unsubscribe/${email}`, { headers });
      setActionMessage(`Successfully unsubscribed ${email}.`);
      // Refresh lists
      fetchSubscribedUsers();
      fetchUnsubscribedUsers();
      setTimeout(() => setActionMessage(''), 3000);
    } catch (err) {
      console.error('Error unsubscribing user:', err);
      setActionMessage(err.response?.data?.error || `Failed to unsubscribe ${email}.`);
      setTimeout(() => setActionMessage(''), 3000);
    }
  };

  const renderUserTable = (users, title, isLoading, showUnsubscribe = false) => (
    <div className="mb-8">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">{title} ({users.length})</h2>
      {isLoading ? (
        <p>Loading users...</p>
      ) : users.length === 0 ? (
        <p>No users in this list.</p>
      ) : (
        <div className="overflow-x-auto bg-white shadow-md rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscribed Date</th>
                {showUnsubscribe && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user._id || user.email}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                  {showUnsubscribe && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleUnsubscribeUser(user.email)}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        Unsubscribe
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">User Management</h1>
        
        {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert"><p>{error}</p></div>}
        {actionMessage && 
          <div className={`p-4 mb-4 text-sm rounded-lg ${actionMessage.includes('Successfully') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} role="alert">
            {actionMessage}
          </div>
        }

        {renderUserTable(subscribedUsers, 'Subscribed Users', loadingSubscribed, true)}
        {renderUserTable(unsubscribedUsers, 'Unsubscribed Users', loadingUnsubscribed, false)}
      </div>
    </AdminLayout>
  );
}

export default AdminUserManagement; 